/**
 * ConnectionsHub — one Settings tab for third-party integrations, behind a segmented control
 * (not tabs-under-tabs): GitHub, Google Ads, Image Storage, Amazon Affiliate, News API, Email spy and
 * Usage. Consolidating them frees up the top-level Settings tab bar. The active sub-view is
 * kept in the URL as `?tab=connections&sub=<github|google-ads|image-storage|amazon|news|email-spy|usage>`
 * so it's linkable + refreshable.
 *
 * Usage is the metered APIs' half of the same story the connection screens tell: those say
 * whether a key is configured, this says what it has been spent on. It sits here rather than
 * under Network because Google Ads and GNews are API integrations, not crawling.
 */
import { useSearchParams } from 'react-router-dom';
import { SubTabs } from '../components/common/Tabs';
import { useAuth } from '../contexts/AuthContext';
import GitHubSettings from './GitHubSettings';
import GoogleAdsSettings from './GoogleAdsSettings';
import ImageStorageSettingsPage from './ImageStorageSettings';
import AmazonAffiliateSettingsPage from './AmazonAffiliateSettings';
import NewsApiSettings from './NewsApiSettings';
import EmailSpySettingsPage from './EmailSpySettings';
import { useEntitlements } from '../contexts/EntitlementContext';
import { subAppliesToLabel, subIsRelevant } from './settingsScope';
import ApiUsageReportPage from './ApiUsageReport';
import AdminPill from '../components/common/AdminPill';

type ConnSub = 'github' | 'google-ads' | 'image-storage' | 'amazon' | 'news' | 'email-spy' | 'usage';
const SUB_KEYS: ConnSub[] = [
  'github',
  'google-ads',
  'image-storage',
  'amazon',
  'news',
  'email-spy',
  'usage',
];
const SUB_CONFIG: { key: ConnSub; label: string; adminOnly?: boolean }[] = [
  { key: 'github', label: 'GitHub' },
  // Admin-only: every keyword request runs through our own manager account, so a
  // non-admin connecting a personal Google Ads account could only ever fail. They
  // use the shared "house" connection an admin grants them instead. See
  // jcseo.keywords.google_ads_access on the backend.
  { key: 'google-ads', label: 'Google Ads', adminOnly: true },
  { key: 'image-storage', label: 'Image Storage' },
  { key: 'amazon', label: 'Amazon Affiliate' },
  // Admin-only: GNews bills per key, not per user, so this is one install-wide
  // credential rather than something each person connects for themselves.
  { key: 'news', label: 'News API', adminOnly: true },
  { key: 'email-spy', label: 'Email spy' },
  // Admin-only. This USED to be open on the reasoning that everyone can see what
  // they themselves spent — true, but the premise changed: a customer is not
  // meant to be reasoning about our API spend at all. The user filter inside the
  // panel stays admin-gated on top (see UsageUserFilter).
  { key: 'usage', label: 'Usage', adminOnly: true },
];

export default function ConnectionsHub({
  initialSub = 'github',
}: {
  initialSub?: ConnSub;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const { held } = useEntitlements();

  // TWO GATES, and the product one is why a Beats-only account keeps its News
  // API key. Connections is a hub of unrelated credentials: GitHub deploys a
  // site, GNews feeds both a beat and a trend brief. Scoping only the TAB meant
  // tagging the whole hub Publish, which would have hidden this screen — and
  // that key — from anyone who had not bought Publish.
  const visibleSubs = SUB_CONFIG.filter(
    (s) => (!s.adminOnly || isAdmin) && subIsRelevant(s.key, held),
  );
  const allowedKeys = visibleSubs.map((s) => s.key);

  const raw = searchParams.get('sub');
  const requested: ConnSub = SUB_KEYS.includes(raw as ConnSub)
    ? (raw as ConnSub)
    : initialSub;
  // A non-admin deep-linking to ?sub=google-ads falls back rather than rendering
  // an empty pane under a tab bar that has no matching button. The fallback is the
  // first one this account sees: a recon-only account has Email spy, not GitHub.
  const sub: ConnSub = allowedKeys.includes(requested) ? requested : allowedKeys[0] ?? 'github';

  const setSub = (s: ConnSub) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'connections');
    if (s === 'github') params.delete('sub');
    else params.set('sub', s);
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      <SubTabs
        active={sub}
        onChange={setSub}
        description="The outside services this account is wired into, and what each has been used for."
        tabs={visibleSubs.map((s) => ({
          key: s.key,
          label: s.label,
          // Same amber pill the admin-only top-level Settings tabs carry, so
          // "only admins see this" reads the same at both levels.
          badge: s.adminOnly ? <AdminPill /> : undefined,
        }))}
      />

      {/* The sub-view's own scope, not the hub's. GitHub is Publish; the News
          API key feeds a beat AND a trend brief. Saying so here is what makes
          showing every credential in one place honest rather than confusing. */}
      {subAppliesToLabel(sub, held) && (
        <p className="-mt-2 mb-4 text-xs text-gray-400 dark:text-gray-500">
          {subAppliesToLabel(sub, held)}
        </p>
      )}

      {sub === 'github' && <GitHubSettings />}
      {sub === 'google-ads' && isAdmin && <GoogleAdsSettings />}
      {sub === 'image-storage' && <ImageStorageSettingsPage />}
      {sub === 'amazon' && <AmazonAffiliateSettingsPage />}
      {sub === 'news' && isAdmin && <NewsApiSettings />}
      {sub === 'email-spy' && <EmailSpySettingsPage />}
      {sub === 'usage' && isAdmin && <ApiUsageReportPage />}
    </div>
  );
}
